import React, { useEffect, useState } from "react";
import { onValue, ref, set } from "firebase/database";
import { database } from "../../../utils/firebase";
import { useNavigate, useParams } from "react-router-dom";
import {Breadcrumbs, Button, Dropdown, Link, Menu, MenuButton, MenuItem, Typography} from "@mui/joy";
import PrintIcon from "@mui/icons-material/Print";
import QuickReportGenerator from "../QuickReportGenerator/QuickReportGenerator";
import {DateTimePicker} from "@mui/x-date-pickers/DateTimePicker";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const EditHandicaps = () => {
    const { leagueId, handicapId } = useParams();
    const navigate = useNavigate();
    const [flightIds, setFlightIds] = useState([]);
    const [handicapData, setHandicapData] = useState({});
    const [leagueData, setLeagueData] = useState({});
    const [isSaved, setIsSaved] = useState(false);
    const [isEdited, setIsEdited] = useState(false);
    const [error, setError] = useState('');
    const [playerIds, setPlayerIds] = useState([]);
    const [handicaps, setHandicaps] = useState({});
    const [handicapName, setHandicapName] = useState('');
    const [startDate, setStartDate] = useState(dayjs());
    const [endDate, setEndDate] = useState(dayjs());

    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [reportType, setReportType] = useState('');

    const leagueRef = ref(database, `leagues/${leagueId}`);
    const handicapRef = ref(database, `leagues/${leagueId}/handicaps/${handicapId}`);

    useEffect(() => {
        onValue(leagueRef, (snapshot) => {
            const data = snapshot.val();
            setLeagueData(data);

            const handicapStartDate = handicapData.startDate * 1000; // Convert to milliseconds
            const handicapEndDate = handicapData.endDate * 1000; // Convert to milliseconds

            const filteredPlayerIds = Object.keys(data.players).filter(playerId => {
                const player = data.players[playerId];
                const playerStartDate = player.startDate;
                const playerEndDate = player.endDate || Infinity; // Use Infinity if no end date is provided

                return (player.status === 'active' && playerStartDate <= handicapEndDate) ||
                       (playerStartDate <= handicapEndDate && handicapStartDate <= playerEndDate);
            });

            setPlayerIds(filteredPlayerIds);
        });

        onValue(handicapRef, (snapshot) => {
            const data = snapshot.val();
            setHandicapData(data);
            if (data && data.flights) {
                setFlightIds(Object.keys(data.flights));
            }
            if (data && data.playerHandicaps) {
                setHandicaps(data.playerHandicaps);
            }
            if (data) {
                setHandicapName(data.name);
                setStartDate(dayjs(data.startDate * 1000));
                setEndDate(dayjs(data.endDate * 1000));
            }
        });
    }, [leagueId, handicapId, handicapData.startDate, handicapData.endDate]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (isEdited) {
                event.preventDefault();
                event.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isEdited]);

    const evenlyDistributeHandicaps = () => {
        const sortedHandicaps = Object.values(handicaps).sort((a, b) => a - b);
        const totalPlayers = sortedHandicaps.length;
        const flights = [];

        for (let i = 0; i < flightIds.length; i++) {
            const start = i === 0 ? 0 : flights[i - 1].end + 1;
            const endIndex = Math.floor((i + 1) * totalPlayers / flightIds.length) - 1;
            const end = sortedHandicaps[endIndex] || sortedHandicaps[totalPlayers - 1];
            flights.push({ start, end });
        }

        const updatedFlights = flightIds.reduce((acc, flightId, i) => {
            acc[flightId] = {
                ...handicapData.flights[flightId],
                start: flights[i].start,
                end: flights[i].end
            };
            return acc;
        }, {});

        setHandicapData((prevData) => ({
            ...prevData,
            flights: updatedFlights
        }));
    };

    const handleFlightChange = (flightId, field, value) => {
        setIsEdited(true);
        setHandicapData((prevData) => ({
            ...prevData,
            flights: {
                ...prevData.flights,
                [flightId]: {
                    ...prevData.flights[flightId],
                    [field]: value
                }
            }
        }));
    };

    const handleHandicapChange = (playerId, value) => {
        setIsEdited(true);
        setHandicaps((prevHandicaps) => ({
            ...prevHandicaps,
            [playerId]: Number(value)
        }));
    };

    const handleAddFlight = () => {
        const newFlightId = `flight-${Date.now()}`;
        setIsEdited(true);
        setHandicapData((prevData) => ({
            ...prevData,
            flights: {
                ...prevData.flights,
                [newFlightId]: {
                    name: '',
                    start: '',
                    end: ''
                }
            }
        }));
        setFlightIds((prevIds) => [...prevIds, newFlightId]);
    };

    const handleRemoveFlight = (flightId) => {
        setIsEdited(true);
        setHandicapData((prevData) => {
            const newFlights = { ...prevData.flights };
            delete newFlights[flightId];
            return { ...prevData, flights: newFlights };
        });
        setFlightIds((prevIds) => prevIds.filter((id) => id !== flightId));
    };

    const validateFlights = () => {
        if (!handicapData.flights) {
            return true; // No flights to validate
        }
        const flights = Object.values(handicapData.flights).sort((a, b) => a.start - b.start);
        for (let i = 0; i < flights.length - 1; i++) {
            if (flights[i].end >= flights[i + 1].start) {
                return false;
            }
        }
        return true;
    };

    const handleFlightSave = async () => {
        if (!validateFlights()) {
            setError('Flight ranges overlap. Please correct them.');
            return;
        }
        try {
            await set(handicapRef, { ...handicapData, playerHandicaps: handicaps, name: handicapName, startDate: Math.floor(startDate.unix()), endDate: Math.floor(endDate.unix()) });
            setIsSaved(true);
            setIsEdited(false);
            setError('');
            setTimeout(() => setIsSaved(false), 1000);
        } catch (error) {
            console.error("Error saving flights: ", error);
        }
    };

    const getFlightForHandicap = (handicap) => {
        for (const flightId of flightIds) {
            const flight = handicapData.flights[flightId];
            const flightStart = Number(flight.start);
            const flightEnd = Number(flight.end);
            if (handicap >= flightStart && handicap <= flightEnd) {
                return flight.name;
            }
        }
        return 'No Flight';
    };

    const getPlayerCountForFlight = (flightId) => {
        const flight = handicapData.flights[flightId];
        const flightStart = Number(flight.start);
        const flightEnd = Number(flight.end);
        return Object.values(handicaps).filter(handicap => handicap >= flightStart && handicap <= flightEnd).length;
    };

    return (
        <div className="container">
            <Breadcrumbs aria-label="breadcrumb">
                <Link underline="hover" color="inherit" onClick={() => navigate(`/app/league/${leagueId}/dashboard`)}>
                    {leagueData.name}
                </Link>
                <Link underline="hover" color="inherit" onClick={() => navigate(`/app/league/${leagueId}/handicaps`)}>
                    Handicaps
                </Link>
                <Typography sx={{ color: 'text.primary' }}>{handicapName}</Typography>
            </Breadcrumbs>
            <div className={"page-head-div"}>
                <h1>Handicaps</h1>
                <Dropdown>
                    <MenuButton variant="plain" sx={{width: "35px", height: "35px"}}><PrintIcon/></MenuButton>
                    <Menu>
                        <MenuItem onClick={
                            () => {
                                setReportModalOpen(true);
                                setReportType('handicapReport');
                            }}>Handicap Report</MenuItem>
                    </Menu>
                </Dropdown>
            </div>
            <div className="dashboard">
                <h1>Handicap Set Settings</h1>
                <div style={{display: 'flex', flexDirection: 'column', width: '400px'}}>
                    Name
                    <input className={'styled-select'} value={handicapName} onChange={(e) => setHandicapName(e.target.value)} />
                    <div style={{display: 'flex', flexDirection: 'row', gap: '0px'}}>
                        <div style={{width: '180px', paddingRight: '40px'}}>
                            Start Date
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker className={'styled-select'} value={startDate} onChange={(date) => setStartDate(date)} />
                            </LocalizationProvider>
                        </div>
                        <div style={{width: '180px'}}>
                            End Date
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker className={'styled-select'} value={endDate} onChange={(date) => setEndDate(date)} />
                            </LocalizationProvider>
                        </div>
                    </div>
                </div>

                <div className={'split-row'}>
                    <h2>Flights</h2>
                    <div className = {'split-buttons'}>
                        <Button className={'button-gradient short-button'} onClick={handleAddFlight}>Add Flight</Button>
                        <Button className={'button-gradient short-button'} onClick={evenlyDistributeHandicaps}>Evenly Distribute Flights</Button>
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {flightIds.map((flightId) => (
                        <div key={flightId} className="ldb-match split-row">
                            <div>
                                <input
                                    className={'styled-select match-score-input-long'}
                                    value={handicapData.flights[flightId]?.name ?? ''}
                                    onChange={(e) => handleFlightChange(flightId, 'name', e.target.value)}
                                />
                                <input
                                    className={'match-score-input styled-select'}
                                    value={handicapData.flights[flightId]?.start ?? ''}
                                    onChange={(e) => handleFlightChange(flightId, 'start', e.target.value)}
                                /> -
                                <input
                                    className={'match-score-input styled-select'}
                                    value={handicapData.flights[flightId]?.end ?? ''}
                                    onChange={(e) => handleFlightChange(flightId, 'end', e.target.value)}
                                />
                                <span>({getPlayerCountForFlight(flightId)} players)</span>
                            </div>
                            <div>
                                <Button className={'button-gradient'} onClick={() => handleRemoveFlight(flightId)}>Remove</Button>
                            </div>
                        </div>
                    ))}
                </div>
                {error && <p className="error-message">{error}</p>}
                <h2>Handicaps</h2>
                <div className="ldb-matches-list">
                    {playerIds.map((playerId) => (
                        <div className="ldb-match" key={playerId}>
                            <p className={'match-person-name'}>{leagueData.players[playerId].name}</p>
                            <input
                                className={'match-score-input styled-select'}
                                value={handicaps[playerId] ?? ''}
                                onChange={(e) => handleHandicapChange(playerId, e.target.value)}
                            />
                            <p className={'match-person-flight'} style={{paddingLeft: "50px"}}>{getFlightForHandicap(handicaps[playerId])}</p>
                        </div>
                    ))}
                </div>
                <Button className={'button-gradient'} onClick={handleFlightSave}>
                    {isSaved ? 'Saved' : 'Save'}
                </Button>
            </div>
            <QuickReportGenerator
                type={reportType}
                id={handicapId}
                modalOpen={reportModalOpen}
                closeModal={() => setReportModalOpen(false)}
                leagueId={leagueId}
            />
        </div>
    );
};

export default EditHandicaps;